import { useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { navigatorUtils } from '@src/shared/lib';
import { useToast } from '@src/app/notifications/toast';
import { IconButton } from '@src/shared/ui/Button';
import { ContentCopyIcon } from '@src/shared/ui/icons';
import Tooltip from '@src/shared/ui/Tooltip';

const COPIED_RESET_MS = 2000;

interface CopyTextButtonProps {
  text: string;
  disabled?: boolean;
}

function CopyContentButton({ text, disabled }: CopyTextButtonProps) {
  const { t } = useTranslation();
  const toast = useToast();
  const [copied, setCopied] = useState(false);

  const handleCopy = useCallback(() => {
    navigatorUtils
      .copyToClipboard(text)
      .then(() => {
        setCopied(true);
        toast.success(t('common.copy_button_copied', 'Copied!'));
        setTimeout(() => setCopied(false), COPIED_RESET_MS);
      })
      .catch(() => {
        toast.error(t('common.copy_button_error', 'Failed to copy'));
      });
  }, [text, toast, t]);

  const tooltipTitle = copied
    ? t('common.copy_button_copied', 'Copied!')
    : t('common.copy_button', 'Copy');

  const button = (
    <IconButton
      aria-label={tooltipTitle}
      disabled={disabled}
      onClick={handleCopy}
      size="small"
    >
      <ContentCopyIcon sx={{ color: 'grey.600' }} fontSize="small" />
    </IconButton>
  );

  if (disabled) return button;

  return <Tooltip title={tooltipTitle}>{button}</Tooltip>;
}

export default CopyContentButton;
